
import React, { useState, useEffect } from "react";
import { apiService } from "@/api/api";
import { useToast } from "@/hooks/use-toast";
import { Activity, Users, Zap } from "lucide-react";
import MetricCard from "./MetricCard";

const DashboardMetrics = () => {
  const [metrics, setMetrics] = useState({
    liveSessions: 0,
    dictationSessions: 0,
    totalPatients: 0
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        
        // Fetch sessions and clients data
        const [sessionsResponse, clientsResponse] = await Promise.all([
          apiService.getSessionsByCaregiver(),
          apiService.getClients()
        ]);

        // Log the actual data structure for debugging
        console.log("Sessions response:", sessionsResponse);
        console.log("Clients response:", clientsResponse);

        // Extract data arrays from API responses
        // Handle both direct array responses and wrapped responses
        let sessions = [];
        let clients = [];

        // Handle sessions data
        if (Array.isArray(sessionsResponse)) {
          sessions = sessionsResponse;
        } else if (sessionsResponse?.data && Array.isArray(sessionsResponse.data)) {
          sessions = sessionsResponse.data;
        } else if (sessionsResponse?.success && Array.isArray(sessionsResponse.data)) {
          sessions = sessionsResponse.data;
        }

        // Handle clients data  
        if (Array.isArray(clientsResponse)) {
          clients = clientsResponse;
        } else if (clientsResponse?.data && Array.isArray(clientsResponse.data)) {
          clients = clientsResponse.data;
        } else if (clientsResponse?.success && Array.isArray(clientsResponse.data)) {
          clients = clientsResponse.data;
        }

        console.log("Processed sessions:", sessions);
        console.log("Processed clients:", clients);

        // Calculate metrics
        const liveSessions = sessions.filter(session => 
          session?.session_type === 'consultation'
        ).length;

        const dictationSessions = sessions.filter(session => 
          session?.session_type === 'dictation'
        ).length;

        const totalPatients = clients.length;

        console.log("Calculated metrics:", {
          liveSessions,
          dictationSessions,
          totalPatients
        });

        setMetrics({
          liveSessions,
          dictationSessions,
          totalPatients
        });

      } catch (error) {
        console.error("Error fetching dashboard metrics:", error);
        toast({
          title: "Error",
          description: "Failed to load dashboard metrics.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [toast]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
      <MetricCard
        title="Consultation Sessions"
        value={metrics.liveSessions}
        icon={Activity}
        iconColor="text-green-600"
        bgColor="bg-green-100"
        isLoading={loading}
      />
      
      <MetricCard
        title="Dictation Sessions"
        value={metrics.dictationSessions}
        icon={Zap}
        iconColor="text-blue-600"
        bgColor="bg-blue-100"
        isLoading={loading}
      />
      
      <MetricCard
        title="Total Clients"
        value={metrics.totalPatients}
        icon={Users}
        iconColor="text-purple-600"
        bgColor="bg-purple-100"
        isLoading={loading}
      />
    </div>
  );
};

export default DashboardMetrics;
